import Chart from 'chart.js/auto';

export type ScoreBand = 'poor' | 'average' | 'good' | 'excellent';

export const SCORE_COLOR_TOKENS: Record<ScoreBand, string> = {
  poor: '--mds_brand_appearance_error_default_background-color',
  average: '--mds_brand_appearance_warning_default_background-color',
  good: '--mds_brand_appearance_info_default_background-color',
  excellent: '--mds_brand_appearance_success_default_background-color',
};

/** Creates and appends a styled tooltip div into `container`. */
export function createTooltipElement(container: HTMLElement, shadow: string, cssClass: string): HTMLElement {
  const el = document.createElement('div');
  el.className = cssClass;
  Object.assign(el.style, {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: '10',
    minWidth: '170px',
    opacity: '0',
    transition: 'opacity 0.15s',
    background: 'var(--mds_brand_appearance_neutral_weakest_background-color)',
    border: '1px solid var(--mds_brand_appearance_neutral_default_border-color)',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: shadow,
    fontSize: '12px',
    fontFamily: 'inherit',
    color: 'var(--mds_brand_appearance_neutral_default_text-color)',
  });
  container.appendChild(el);
  return el;
}

/**
 * Watches document.head for the theme <link> to be added and calls `rebuild` once
 * the stylesheet has loaded (with a 50 ms fallback for cached stylesheets).
 * Returns the MutationObserver so the caller can disconnect it on destroy.
 */
export function buildThemeObserver(rebuild: () => void): MutationObserver {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if ((node as Element).id === 'theme-css-id') {
          const link = node as HTMLLinkElement;
          let fired = false;
          const once = () => {
            if (fired) return;
            fired = true;
            rebuild();
          };
          link.addEventListener('load', once, { once: true });
          setTimeout(once, 50); // fallback for cached stylesheets where load may not fire
          return;
        }
      }
    }
  });
  observer.observe(document.head, { childList: true });
  return observer;
}

/** Destroys a Chart instance and removes its tooltip element. */
export function destroyChartParts(chart: Chart | null, tooltipEl: HTMLElement | null): void {
  chart?.destroy();
  tooltipEl?.remove();
}
